/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const COLLECT_FEES_DISCRIMINATOR = new Uint8Array([
  164, 152, 207, 99, 30, 186, 19, 182,
]);

export function getCollectFeesDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    COLLECT_FEES_DISCRIMINATOR,
  );
}

export type CollectFeesInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountPosition extends string | AccountMeta<string> = string,
  TAccountOwner extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountVault0 extends string | AccountMeta<string> = string,
  TAccountVault1 extends string | AccountMeta<string> = string,
  TAccountToken0Mint extends string | AccountMeta<string> = string,
  TAccountToken1Mint extends string | AccountMeta<string> = string,
  TAccountUser0 extends string | AccountMeta<string> = string,
  TAccountUser1 extends string | AccountMeta<string> = string,
  TAccountToken0Program extends string | AccountMeta<string> = string,
  TAccountToken1Program extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountPosition extends string
        ? WritableAccount<TAccountPosition>
        : TAccountPosition,
      TAccountOwner extends string
        ? ReadonlySignerAccount<TAccountOwner> &
            AccountSignerMeta<TAccountOwner>
        : TAccountOwner,
      TAccountAuthority extends string
        ? ReadonlyAccount<TAccountAuthority>
        : TAccountAuthority,
      TAccountVault0 extends string
        ? WritableAccount<TAccountVault0>
        : TAccountVault0,
      TAccountVault1 extends string
        ? WritableAccount<TAccountVault1>
        : TAccountVault1,
      TAccountToken0Mint extends string
        ? ReadonlyAccount<TAccountToken0Mint>
        : TAccountToken0Mint,
      TAccountToken1Mint extends string
        ? ReadonlyAccount<TAccountToken1Mint>
        : TAccountToken1Mint,
      TAccountUser0 extends string
        ? WritableAccount<TAccountUser0>
        : TAccountUser0,
      TAccountUser1 extends string
        ? WritableAccount<TAccountUser1>
        : TAccountUser1,
      TAccountToken0Program extends string
        ? ReadonlyAccount<TAccountToken0Program>
        : TAccountToken0Program,
      TAccountToken1Program extends string
        ? ReadonlyAccount<TAccountToken1Program>
        : TAccountToken1Program,
      ...TRemainingAccounts,
    ]
  >;

export type CollectFeesInstructionData = {
  discriminator: ReadonlyUint8Array;
  max0: bigint;
  max1: bigint;
};

export type CollectFeesInstructionDataArgs = {
  max0: number | bigint;
  max1: number | bigint;
};

export function getCollectFeesInstructionDataEncoder(): FixedSizeEncoder<CollectFeesInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['max0', getU64Encoder()],
      ['max1', getU64Encoder()],
    ]),
    (value) => ({ ...value, discriminator: COLLECT_FEES_DISCRIMINATOR }),
  );
}

export function getCollectFeesInstructionDataDecoder(): FixedSizeDecoder<CollectFeesInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['max0', getU64Decoder()],
    ['max1', getU64Decoder()],
  ]);
}

export function getCollectFeesInstructionDataCodec(): FixedSizeCodec<
  CollectFeesInstructionDataArgs,
  CollectFeesInstructionData
> {
  return combineCodec(
    getCollectFeesInstructionDataEncoder(),
    getCollectFeesInstructionDataDecoder(),
  );
}

export type CollectFeesAsyncInput<
  TAccountPool extends string = string,
  TAccountPosition extends string = string,
  TAccountOwner extends string = string,
  TAccountAuthority extends string = string,
  TAccountVault0 extends string = string,
  TAccountVault1 extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountUser0 extends string = string,
  TAccountUser1 extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
> = {
  pool: Address<TAccountPool>;
  position: Address<TAccountPosition>;
  owner: TransactionSigner<TAccountOwner>;
  authority?: Address<TAccountAuthority>;
  vault0: Address<TAccountVault0>;
  vault1: Address<TAccountVault1>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  user0: Address<TAccountUser0>;
  user1: Address<TAccountUser1>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  max0: CollectFeesInstructionDataArgs['max0'];
  max1: CollectFeesInstructionDataArgs['max1'];
};

export async function getCollectFeesInstructionAsync<
  TAccountPool extends string,
  TAccountPosition extends string,
  TAccountOwner extends string,
  TAccountAuthority extends string,
  TAccountVault0 extends string,
  TAccountVault1 extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountUser0 extends string,
  TAccountUser1 extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: CollectFeesAsyncInput<
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CollectFeesInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    position: { value: input.position ?? null, isWritable: true },
    owner: { value: input.owner ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: false },
    vault0: { value: input.vault0 ?? null, isWritable: true },
    vault1: { value: input.vault1 ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    user0: { value: input.user0 ?? null, isWritable: true },
    user1: { value: input.user1 ?? null, isWritable: true },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.authority.value) {
    accounts.authority.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([97, 117, 116, 104, 111, 114, 105, 116, 121]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('position', accounts.position),
      getAccountMeta('owner', accounts.owner),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vault0', accounts.vault0),
      getAccountMeta('vault1', accounts.vault1),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('user0', accounts.user0),
      getAccountMeta('user1', accounts.user1),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
    ],
    data: getCollectFeesInstructionDataEncoder().encode(
      args as CollectFeesInstructionDataArgs,
    ),
    programAddress,
  } as CollectFeesInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program
  >);
}

export type CollectFeesInput<
  TAccountPool extends string = string,
  TAccountPosition extends string = string,
  TAccountOwner extends string = string,
  TAccountAuthority extends string = string,
  TAccountVault0 extends string = string,
  TAccountVault1 extends string = string,
  TAccountToken0Mint extends string = string,
  TAccountToken1Mint extends string = string,
  TAccountUser0 extends string = string,
  TAccountUser1 extends string = string,
  TAccountToken0Program extends string = string,
  TAccountToken1Program extends string = string,
> = {
  pool: Address<TAccountPool>;
  position: Address<TAccountPosition>;
  owner: TransactionSigner<TAccountOwner>;
  authority: Address<TAccountAuthority>;
  vault0: Address<TAccountVault0>;
  vault1: Address<TAccountVault1>;
  token0Mint: Address<TAccountToken0Mint>;
  token1Mint: Address<TAccountToken1Mint>;
  user0: Address<TAccountUser0>;
  user1: Address<TAccountUser1>;
  token0Program: Address<TAccountToken0Program>;
  token1Program: Address<TAccountToken1Program>;
  max0: CollectFeesInstructionDataArgs['max0'];
  max1: CollectFeesInstructionDataArgs['max1'];
};

export function getCollectFeesInstruction<
  TAccountPool extends string,
  TAccountPosition extends string,
  TAccountOwner extends string,
  TAccountAuthority extends string,
  TAccountVault0 extends string,
  TAccountVault1 extends string,
  TAccountToken0Mint extends string,
  TAccountToken1Mint extends string,
  TAccountUser0 extends string,
  TAccountUser1 extends string,
  TAccountToken0Program extends string,
  TAccountToken1Program extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: CollectFeesInput<
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program
  >,
  config?: { programAddress?: TProgramAddress },
): CollectFeesInstruction<
  TProgramAddress,
  TAccountPool,
  TAccountPosition,
  TAccountOwner,
  TAccountAuthority,
  TAccountVault0,
  TAccountVault1,
  TAccountToken0Mint,
  TAccountToken1Mint,
  TAccountUser0,
  TAccountUser1,
  TAccountToken0Program,
  TAccountToken1Program
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    position: { value: input.position ?? null, isWritable: true },
    owner: { value: input.owner ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: false },
    vault0: { value: input.vault0 ?? null, isWritable: true },
    vault1: { value: input.vault1 ?? null, isWritable: true },
    token0Mint: { value: input.token0Mint ?? null, isWritable: false },
    token1Mint: { value: input.token1Mint ?? null, isWritable: false },
    user0: { value: input.user0 ?? null, isWritable: true },
    user1: { value: input.user1 ?? null, isWritable: true },
    token0Program: { value: input.token0Program ?? null, isWritable: false },
    token1Program: { value: input.token1Program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('position', accounts.position),
      getAccountMeta('owner', accounts.owner),
      getAccountMeta('authority', accounts.authority),
      getAccountMeta('vault0', accounts.vault0),
      getAccountMeta('vault1', accounts.vault1),
      getAccountMeta('token0Mint', accounts.token0Mint),
      getAccountMeta('token1Mint', accounts.token1Mint),
      getAccountMeta('user0', accounts.user0),
      getAccountMeta('user1', accounts.user1),
      getAccountMeta('token0Program', accounts.token0Program),
      getAccountMeta('token1Program', accounts.token1Program),
    ],
    data: getCollectFeesInstructionDataEncoder().encode(
      args as CollectFeesInstructionDataArgs,
    ),
    programAddress,
  } as CollectFeesInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountAuthority,
    TAccountVault0,
    TAccountVault1,
    TAccountToken0Mint,
    TAccountToken1Mint,
    TAccountUser0,
    TAccountUser1,
    TAccountToken0Program,
    TAccountToken1Program
  >);
}

export type ParsedCollectFeesInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    pool: TAccountMetas[0];
    position: TAccountMetas[1];
    owner: TAccountMetas[2];
    authority: TAccountMetas[3];
    vault0: TAccountMetas[4];
    vault1: TAccountMetas[5];
    token0Mint: TAccountMetas[6];
    token1Mint: TAccountMetas[7];
    user0: TAccountMetas[8];
    user1: TAccountMetas[9];
    token0Program: TAccountMetas[10];
    token1Program: TAccountMetas[11];
  };
  data: CollectFeesInstructionData;
};

export function parseCollectFeesInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCollectFeesInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 12) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 12,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      pool: getNextAccount(),
      position: getNextAccount(),
      owner: getNextAccount(),
      authority: getNextAccount(),
      vault0: getNextAccount(),
      vault1: getNextAccount(),
      token0Mint: getNextAccount(),
      token1Mint: getNextAccount(),
      user0: getNextAccount(),
      user1: getNextAccount(),
      token0Program: getNextAccount(),
      token1Program: getNextAccount(),
    },
    data: getCollectFeesInstructionDataDecoder().decode(instruction.data),
  };
}
